// ChartVisualization.js
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { SECURITY_PILLARS } from '../../../constants';

const ChartVisualization = ({
  businessValues,
  selectedValueForChart,
  setSelectedValueForChart,
  zoomLevel,
  setZoomLevel,
  viewportPosition,
  setViewportPosition,
  isDragging,
  setIsDragging,
  dragStart,
  setDragStart,
  useShortIds,
  chartContainerRef
}) => {
  const { t } = useTranslation();

  // State for node positions
  const [nodePositions, setNodePositions] = useState({});
  const [draggingNodeId, setDraggingNodeId] = useState(null);

  // Initialize positions in a circle
  useEffect(() => {
    const newPositions = { ...nodePositions };
    let hasChanges = false;

    businessValues.forEach((value, index) => {
      if (!newPositions[value.id]) {
        const angle = (Math.PI * 2 * index) / businessValues.length;
        const radius = 200; // Distance from center
        newPositions[value.id] = {
          x: 400 + radius * Math.cos(angle),
          y: 300 + radius * Math.sin(angle)
        };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setNodePositions(newPositions);
    }
  }, [businessValues]);

  // Fonction pour gérer le zoom
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  const handleZoomReset = () => {
    setZoomLevel(0.75); // Reset to 1.5x zoom out (0.75 zoom level)
    setViewportPosition({ x: 0, y: 0 });
  };

  // Gestion du déplacement dans la vue (Pan)
  const handleMouseDown = (e) => {
    if (e.button === 0) { // Click gauche uniquement
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  // Gestion du déplacement des noeuds (Drag Node)
  const handleNodeMouseDown = (e, id) => {
    e.stopPropagation(); // Prevent panning when dragging a node
    if (e.button === 0) {
      setDraggingNodeId(id);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (draggingNodeId) {
      const dx = (e.clientX - dragStart.x) / zoomLevel; // Adjust for zoom
      const dy = (e.clientY - dragStart.y) / zoomLevel;

      setNodePositions(prev => ({
        ...prev,
        [draggingNodeId]: {
          x: prev[draggingNodeId].x + dx,
          y: prev[draggingNodeId].y + dy
        }
      }));
      setDragStart({ x: e.clientX, y: e.clientY });
    } else if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setViewportPosition(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggingNodeId(null);
  };

  // Rechercher la valeur métier sélectionnée
  const selectedBusinessValue = businessValues.find(value => value.id === selectedValueForChart);

  return (
    <div className="lg:w-2/3 border rounded-lg p-4 bg-gray-50 overflow-hidden flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-gray-700">{t('businessValues.chart.globalMapping')}</h3>

        {/* Contrôles de zoom */}
        <div className="flex space-x-2">
          <button
            className="bg-white border rounded-md p-1 text-gray-600 hover:bg-gray-100 shadow-sm transition-colors"
            onClick={handleZoomIn}
            title={t('businessValues.chart.zoomIn')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
          <button
            className="bg-white border rounded-md p-1 text-gray-600 hover:bg-gray-100 shadow-sm transition-colors"
            onClick={handleZoomOut}
            title={t('businessValues.chart.zoomOut')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
            </svg>
          </button>
          <button
            className="bg-white border rounded-md p-1 text-gray-600 hover:bg-gray-100 shadow-sm transition-colors"
            onClick={handleZoomReset}
            title={t('businessValues.chart.resetView')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <div
        ref={chartContainerRef}
        className="relative overflow-hidden cursor-move flex-grow bg-white rounded-lg border border-gray-200 shadow-inner"
        style={{ height: '600px' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Grid Background Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(#cbd5e0 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            transform: `translate(${viewportPosition.x % 20}px, ${viewportPosition.y % 20}px)`
          }}
        />

        {/* Info-bulle pour la valeur sélectionnée - placée en dehors du périmètre */}
        <AnimatePresence>
          {selectedBusinessValue && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg border shadow-lg z-30 max-w-xs"
            >
              <div className="flex items-center mb-1">
                <span className="text-sm font-bold text-blue-600 mr-2 bg-blue-50 px-2 py-0.5 rounded">{selectedBusinessValue.shortId}</span>
                <span className="text-sm font-medium text-gray-800 truncate">{selectedBusinessValue.name}</span>
              </div>
              <div className="text-xs text-gray-500 flex gap-2">
                <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-gray-400 mr-1"></span>{selectedBusinessValue.supportAssets?.length || 0} {t('businessValues.chart.supportAssetCount')}</span>
                <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-blue-500 mr-1"></span>{selectedBusinessValue.securityPillars?.length || 0} {t('businessValues.chart.pillarCount')}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Périmètre d'étude */}
        <motion.div
          className="absolute border-[3px] border-gray-300 border-dashed rounded-xl"
          style={{
            width: `800px`,
            height: `600px`,
            left: `calc(50% - 400px)`,
            top: `calc(50% - 300px)`,
            x: viewportPosition.x,
            y: viewportPosition.y,
            scale: zoomLevel,
            transformOrigin: 'center',
            zIndex: 0
          }}
        >
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white px-3 py-0.5 rounded-full border border-gray-200 shadow-sm">
            <span className="font-medium text-gray-500 text-xs uppercase tracking-wider">{t('businessValues.chart.studyPerimeter')}</span>
          </div>

          {/* Éléments internes qui seront transformés ensemble */}
          <div className="absolute inset-0">
            {/* Cercle central pour chaque valeur métier */}
            {businessValues.map((value) => {
              const pos = nodePositions[value.id];
              if (!pos) return null; // Wait for position initialization

              const x = pos.x;
              const y = pos.y;
              const isSelected = value.id === selectedValueForChart;

              return (
                <React.Fragment key={value.id}>
                  {/* Ligne de connection au centre */}
                  <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
                    <motion.line
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ duration: 0.5 }}
                      x1="400"
                      y1="300"
                      x2={x}
                      y2={y}
                      stroke={isSelected ? "#3182CE" : "#CBD5E0"}
                      strokeWidth={isSelected ? "3" : "2"}
                    />
                  </svg>

                  {/* Cercle de la valeur métier */}
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                    className={`absolute flex items-center justify-center rounded-full
                      border-[3px] bg-white cursor-grab active:cursor-grabbing transition-colors duration-200
                      ${isSelected ? 'border-blue-500 shadow-lg ring-4 ring-blue-100' : 'border-gray-300 shadow-md'}`}
                    style={{
                      width: isSelected ? '110px' : '90px',
                      height: isSelected ? '110px' : '90px',
                      left: x - (isSelected ? 55 : 45),
                      top: y - (isSelected ? 55 : 45),
                      zIndex: isSelected ? 20 : 10
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedValueForChart(value.id);
                    }}
                    onMouseDown={(e) => handleNodeMouseDown(e, value.id)}
                  >
                    <div className="text-center p-1 pointer-events-none select-none">
                      <div className={`font-bold text-sm ${isSelected ? 'text-blue-600' : 'text-gray-700'}`}>
                        {useShortIds ? value.shortId : (
                          value.name.length > 15 ? `${value.name.substring(0, 12)}...` : value.name
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {!useShortIds && <span className="font-semibold block">{value.shortId}</span>}
                        {(value.supportAssets || []).length > 0 &&
                          <span className="bg-gray-100 px-1.5 py-0.5 rounded-full text-[10px] mt-1 inline-block">
                            {(value.supportAssets || []).length} {t('businessValues.chart.assets')}
                          </span>
                        }
                      </div>
                    </div>
                  </motion.div>

                  {/* Petits cercles pour les piliers de sécurité */}
                  {(value.securityPillars || []).map((pillarId, pIndex) => {
                    const pillar = SECURITY_PILLARS.find(p => p.id === pillarId);
                    if (!pillar) return null;

                    const pillarAngle = (Math.PI * 2 * pIndex) / (value.securityPillars || []).length;
                    const pillarRadius = 70;
                    const pX = x + pillarRadius * Math.cos(pillarAngle);
                    const pY = y + pillarRadius * Math.sin(pillarAngle);

                    return (
                      <motion.div
                        key={`${value.id}-${pillarId}`}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2 + pIndex * 0.1 }}
                        className="absolute rounded-full flex items-center justify-center text-white text-xs shadow-sm font-bold border-2 border-white"
                        style={{
                          backgroundColor: pillar.color,
                          width: '32px',
                          height: '32px',
                          left: pX - 16,
                          top: pY - 16,
                          zIndex: 15
                        }}
                        title={t(`securityPillars.${pillar.id}`)}
                      >
                        {t(`securityPillars.${pillar.id}`).substring(0, 1)}
                      </motion.div>
                    );
                  })}

                  {/* Afficher les biens support */}
                  {(value.supportAssets || []).map((asset, assetIndex) => {
                    const totalAssets = value.supportAssets.length;
                    // Distribute assets in a semi-circle or fan shape outward
                    const assetAngleOffset = (Math.PI / 2) * ((assetIndex - (totalAssets - 1) / 2) / Math.max(totalAssets, 1));
                    // Calculate angle from center to node to orient assets outward
                    const angleFromCenter = Math.atan2(y - 300, x - 400);
                    const assetAngle = angleFromCenter + assetAngleOffset;

                    const assetRadius = 130;
                    const aX = x + assetRadius * Math.cos(assetAngle);
                    const aY = y + assetRadius * Math.sin(assetAngle);

                    return (
                      <React.Fragment key={`asset-${asset.id}`}>
                        {/* Ligne pointillée vers le bien support */}
                        <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
                          <motion.line
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            x1={x} y1={y} x2={aX} y2={aY}
                            stroke={isSelected ? "#4299E1" : "#A0AEC0"}
                            strokeWidth={isSelected ? "2" : "1"}
                            strokeDasharray="4 4"
                          />
                        </svg>

                        {/* Bien support */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 + assetIndex * 0.05 }}
                          whileHover={{ scale: 1.1, zIndex: 25 }}
                          className={`absolute flex items-center justify-center rounded-md border text-xs shadow-sm font-medium px-2 py-1
                            ${isSelected ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-gray-300 text-gray-700'}`}
                          style={{
                            minWidth: '80px',
                            maxWidth: '120px',
                            left: aX - 40,
                            top: aY - 15,
                            zIndex: 14
                          }}
                          title={`${asset.shortId} - ${asset.name}`}
                        >
                          {useShortIds ?
                            <span className="font-bold">{asset.shortId}</span> :
                            <span className="truncate">{asset.name}</span>
                          }
                        </motion.div>
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* Cercle central */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.1 }}
              className="absolute rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-inner border-4 border-white ring-1 ring-gray-200"
              style={{
                width: '80px',
                height: '80px',
                left: '360px',
                top: '260px',
                zIndex: 5
              }}
            >
              <div className="flex flex-col items-center">
                <span className="text-sm text-gray-700 font-bold">SE</span>
                <span className="text-[10px] text-gray-500">Système</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ChartVisualization;